"use client";
import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
  children: React.ReactNode;
  showCloseButton?: boolean; // New prop to control close button visibility
  isFullscreen?: boolean; // Default to false for backwards compatibility
  closeOnBackdropClick?: boolean; // Control whether clicking backdrop closes modal (default: false to prevent accidental closures)
}

interface ModalHeaderProps {
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}

// No `maxHeight` here on purpose: the panel owns the height (see modalClasses).
// One was live here until 3c1cf927b (Nov 2025) traded the inline style for the
// flex layout; the declaration outlived it. Use ScrollableModal when a modal
// genuinely needs its own cap, since its maxHeight is real.
interface ModalContentProps {
  children: React.ReactNode;
  className?: string;
  scrollable?: boolean;
}

interface ModalFooterProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'actions';
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  showCloseButton = true, // Default to true for backwards compatibility
  isFullscreen = false,
  closeOnBackdropClick = false, // Default to false to prevent accidental closures
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousIsOpen = useRef<boolean>(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    // Keep track of previous state for debugging
    previousIsOpen.current = isOpen;
    
    // Only modify the body style when the isOpen state changes
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      // Small timeout to ensure the modal is fully closed before enabling scroll
      setTimeout(() => {
        // Only restore scroll if no other modals are open
        if (!document.querySelector(".modal:not(.hidden)")) {
          document.body.style.overflow = "";
        }
      }, 100);
    }

    // Cleanup function - always restore scroll when component unmounts
    return () => {
      // Add a small timeout to ensure any animations complete first
      setTimeout(() => {
        // Check if this was the last open modal before restoring
        if (previousIsOpen.current && !document.querySelector(".modal:not(.hidden)")) {
          document.body.style.overflow = "";
        }
      }, 100);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Requirement summary: fullscreen modals must use the visible mobile viewport and preserve safe-area space for browser controls.
  const contentClasses = isFullscreen
    ? "w-full min-h-[100dvh] bg-white dark:bg-gray-900"
    : "relative w-full rounded-3xl bg-white dark:bg-gray-900";

  // Fullscreen mode: no padding, no max-height, no backdrop blur
  const containerClasses = isFullscreen
    ? "fixed inset-0 min-h-[100dvh] modal z-[999999]"
    : "fixed inset-0 flex items-center justify-center overflow-y-auto modal z-[999999] p-4 sm:p-6 md:py-8";

  const modalClasses = isFullscreen
    ? `${contentClasses} ${className} relative z-[1000000] flex flex-col pb-[env(safe-area-inset-bottom)]`
    : `${contentClasses} ${className} relative z-[1000000] my-auto max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] md:max-h-[calc(100vh-4rem)] flex flex-col`;

  const modalContent = (
    <div className={containerClasses}>
      {!isFullscreen && (
        <div
          className="fixed inset-0 h-full w-full bg-gray-400/50 backdrop-blur-[32px]"
          onClick={closeOnBackdropClick ? onClose : undefined}
        ></div>
      )}
      <div
        ref={modalRef}
        className={modalClasses}
        onClick={(e) => e.stopPropagation()}
      >
        {showCloseButton && (
          <button
            onClick={onClose}
            className="absolute right-3 top-3 z-999 flex h-9.5 w-9.5 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white sm:right-6 sm:top-6 sm:h-11 sm:w-11"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.04289 16.5413C5.65237 16.9318 5.65237 17.565 6.04289 17.9555C6.43342 18.346 7.06658 18.346 7.45711 17.9555L11.9987 13.4139L16.5408 17.956C16.9313 18.3466 17.5645 18.3466 17.955 17.956C18.3455 17.5655 18.3455 16.9323 17.955 16.5418L13.4129 11.9997L17.955 7.4576C18.3455 7.06707 18.3455 6.43391 17.955 6.04338C17.5645 5.65286 16.9313 5.65286 16.5408 6.04338L11.9987 10.5855L7.45711 6.0439C7.06658 5.65338 6.43342 5.65338 6.04289 6.0439C5.65237 6.43442 5.65237 7.06759 6.04289 7.45811L10.5845 11.9997L6.04289 16.5413Z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}
        <div className="flex flex-col min-h-0 flex-1">{children}</div>
      </div>
    </div>
  );

  return mounted ? createPortal(modalContent, document.body) : null;
};

// Modal Header subcomponent with standardized styling
const ModalHeader: React.FC<ModalHeaderProps> = ({ 
  children, 
  className = "",
  size = 'lg'
}) => {
  const getSizeClasses = () => {
    switch(size) {
      case 'sm': return 'text-lg font-medium';
      case 'md': return 'text-xl font-semibold';
      case 'lg': return 'text-2xl font-semibold';
      case 'xl': return 'text-3xl font-semibold';
      default: return 'text-2xl font-semibold';
    }
  };

  return (
    <div className={`px-6 pr-14 sm:pr-20 pt-6 pb-4 flex-shrink-0 ${className}`}>
      <div className={`text-gray-900 dark:text-white ${getSizeClasses()}`}>
        {children}
      </div>
    </div>
  );
};

// Modal Content subcomponent. Scrolls within the panel by default.
//
// `scrollable` defaults to TRUE; most call sites never passed it. The opt-out
// (`scrollable={false}`) renders a plain div with no overflow handling and no
// height constraint, so a modal taller than the viewport spills outside the
// rounded card and its footer detaches from the panel — the only way to reach
// the bottom is the overlay's own overflow-y-auto, which gives the reader no
// affordance that there is more below. That was the default until 2026-08-11,
// when it was caught on SendRosterModal at phone width.
//
// For ordinary flow content the two modes look the same: content that fits
// produces no scrollbar and no chip either way. They are NOT interchangeable for
// an inline absolutely positioned overlay — a dropdown or popover rendered in
// place rather than portaled. `overflow-y-auto` establishes a clipping context,
// so such a panel is cut off at the content box instead of floating over the
// footer. Measured at 1400x900 on the IssueCreditModal shape BEFORE the fix: 204px
// of a 240px results list clipped, one row left visible. The answer is to portal the
// menu, not to opt the modal out — see hooks/ui/useAnchoredMenuPosition, which four
// menus now use, so no call site opts out for that reason. In-place hover tooltips
// do still exist in some billing/POS modals; ui-patterns.md has the inventory.
//
// So reach for `scrollable={false}` only when a modal manages its own scroll
// chain (see PolicySigningModal) — and say so at the call site.
//
// When scrollable and there is more content below the fold, a bouncing arrow
// chip appears at the bottom so users know to scroll. The chip hides once the
// user reaches the bottom and is hidden on mobile so it does not cover form or
// invoice rows.
//
// The forwarded ref exposes the actual scroll container (the inner
// overflow-y-auto div when `scrollable`, the content div otherwise) so
// step-based callers can reset scrollTop on step change — window.scrollTo
// can't reach it (GH #2016/#2018 bug class).
const ModalContent = React.forwardRef<HTMLDivElement, ModalContentProps>(function ModalContent(
  { children, className = "", scrollable = true },
  forwardedRef
) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const setRefs = React.useCallback(
    (node: HTMLDivElement | null) => {
      scrollRef.current = node;
      if (typeof forwardedRef === "function") {
        forwardedRef(node);
      } else if (forwardedRef) {
        forwardedRef.current = node;
      }
    },
    [forwardedRef]
  );
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    if (!scrollable) return;
    const el = scrollRef.current;
    if (!el) return;

    const update = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight + 1;
      const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
      setShowScrollHint(hasOverflow && !atBottom);
    };

    update();
    el.addEventListener("scroll", update, { passive: true });
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", update);
      resizeObserver.disconnect();
    };
  }, [scrollable, children]);

  const handleScrollHintClick = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ top: el.clientHeight * 0.8, behavior: "smooth" });
  };

  if (!scrollable) {
    return <div ref={setRefs} className={`px-6 py-4 ${className}`}>{children}</div>;
  }

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      <div
        ref={setRefs}
        className={`px-6 py-4 overflow-y-auto flex-1 min-h-0 ${className}`}
      >
        {children}
      </div>
      {showScrollHint && (
        <button
          type="button"
          aria-label="Scroll for more"
          onClick={handleScrollHintClick}
          className="pointer-events-auto absolute bottom-3 left-1/2 z-10 hidden h-9 w-9 -translate-x-1/2 items-center justify-center rounded-full bg-white text-gray-700 shadow-lg ring-1 ring-black/5 transition hover:bg-gray-50 motion-safe:animate-bounce dark:bg-gray-800 dark:text-gray-200 dark:ring-white/10 dark:hover:bg-gray-700 sm:flex"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M7 10l5 5 5-5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
    </div>
  );
});

// Modal Footer subcomponent with standardized styling
const ModalFooter: React.FC<ModalFooterProps> = ({ 
  children, 
  className = "",
  variant = 'default'
}) => {
  // flex-wrap is a safety net, not a layout choice: without it an action row that
  // outgrows the panel overflows the START edge (because of justify-end), and
  // start-edge overflow cannot be scrolled to — those buttons become genuinely
  // unreachable rather than merely off-screen. Wrapping only engages once a row
  // is already overflowing, so footers that fit today are unaffected.
  const variantClasses = variant === 'actions'
    ? 'flex-wrap justify-end gap-3'
    : 'flex-wrap justify-center gap-y-3';

  return (
    <div className={`px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 rounded-b-3xl flex-shrink-0 ${className}`}>
      <div className={`flex items-center ${variantClasses}`}>
        {children}
      </div>
    </div>
  );
};

// Type augmentation for the subcomponents
interface ModalComponent extends React.FC<ModalProps> {
  Header: React.FC<ModalHeaderProps>;
  Content: React.ForwardRefExoticComponent<
    ModalContentProps & React.RefAttributes<HTMLDivElement>
  >;
  Footer: React.FC<ModalFooterProps>;
}

// Cast Modal to include subcomponents and attach them
const ModalWithSubcomponents = Modal as ModalComponent;
ModalWithSubcomponents.Header = ModalHeader;
ModalWithSubcomponents.Content = ModalContent;
ModalWithSubcomponents.Footer = ModalFooter;

// Export the enhanced Modal
export { ModalWithSubcomponents as Modal };

// Export ScrollableModal as well
export { default as ScrollableModal } from './ScrollableModal';
